import {
  Box,
  DeleteIcon,
  Divider,
  Edit1Icon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  RiskProfileIcon,
  Show,
  StatusField,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  TermIcon,
  Typography,
  VolatileIcon,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  EquityBasketListInterface,
  SingleStockItemInterface,
} from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { handleRemoveUnderscore, numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { handleCapitalize } from "utils/common";
import { handleCreateFormData } from "utils/formHelpers";
import { extraColumnsField, htmlToText, refetchQuery } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import AssetHistoryDetailsModal from "./AssetHistoryDetailsModal";
import AssetsStatusModal from "./AssetsStatusModal";

const AddPortfolioDetailsModal = () => {
  useDocumentTitle("Basket Asset Details");

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { assetDetailsTabs } = useAppSelector((state) => state?.app?.tab);

  const userData = useAppSelector((state) => state?.user?.userData);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const [showAssetModal, setShowAssetModal] = useState(false);

  const [showAssetDetails, setShowAssetDetails] = useState(null);

  const { id, assetId } = useParams();

  const form = useForm({
    defaultValues: {
      stockName: "",
      recommendedBuyPrice: "",
      rationale: "",
      status: "",
      riskProfile: "",
      published: false,
      timePeriod: "",
      volatility: "",
      targetPrice: "",
      limitPrice: "",
      deviation: "",
      stopLoss: "",
      research_files: [],
    },
  });

  const {
    data: addDetailsData,
    isFetching,
    isRefetching,
  } = useFetchQuery<{ data: SingleStockItemInterface }>({
    key: ["GET_STOCK_DETAILS", id, assetId],
    route: `/model_portfolio/${id}/stock_item/${assetId}/`,
    success: (res: any) => {
      form.reset({
        ...res?.data,
        research_files: [],
      });
    },
  });

  const { mutate: handleSubmit } = useMutation({
    mutationKey: ["ADD_PORTFOLIO_DETAILS"],
    mutationFn: (data: any) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/stock_item/${addDetailsData?.data?.id}/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("GET_STOCK_DETAILS");
    },
  });

  const { mutate: handleDeleteResearchFiles, isLoading: researchFilesLoading } =
    useMutation({
      mutationKey: ["DELETE_RESEARCH_FILES"],
      mutationFn: (data: any) =>
        axiosInstance.delete(
          `/model_portfolio/${id}/stock_item/${addDetailsData?.data?.id}/delete_research_file/?role=advisor&file_id=${data}`,
        ),
      onSuccess: () => {
        refetchQuery("GET_STOCK_DETAILS");
      },
    });

  const {
    mutate: handlePublishResearchFiles,
    isLoading: researchUpdateFilesLoading,
  } = useMutation({
    mutationKey: ["PUBLISH_RESEARCH_FILES"],
    mutationFn: (data: any) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/stock_item/${addDetailsData?.data?.id}/update_research_status/?role=advisor&file_id=${data}`,
      ),
    onSuccess: () => {
      refetchQuery("GET_STOCK_DETAILS");
    },
  });

  /** ========================================================================================================= */
  const { data } = useFetchQuery({
    key: ["GET_EQUITY_BASKET_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    enabled: Boolean(id),
  });

  const basketDetails: EquityBasketListInterface = data?.data;

  const tabsData = [
    {
      show: true,
      title: "Assets History",
      element: (
        <Show.When
          isTrue={addDetailsData?.data?.stockUniverseItemHistory?.length !== 0}
          showNodata
        >
          <GKClientDataGrid
            name="assetHistory"
            density="compact"
            columnsButton={false}
            pagination={false}
            rows={addDetailsData?.data?.stockUniverseItemHistory || []}
            onRowClick={(params) => {
              setShowAssetDetails({
                ...params.row,
                name: addDetailsData?.data?.companyName,
              });
              setShowAssetModal(true);
            }}
            columns={[
              {
                ...extraColumnsField("Date", "date", 1, 1),
                valueFormatter: (params) => params.value || "-",
              },
              // {
              //   ...extraColumnsField("Status", "assetStatus", 1, 1),
              //   valueFormatter: (params) => params.value || "-",
              //   renderCell: (params) => {
              //     return (
              //       <StatusField
              //         statusString={params.row.assetStatus || "N/A"}
              //       />
              //     );
              //   },
              // },
              {
                ...extraColumnsField("Target Price", "targetPrice", 1, 1),
                valueFormatter: (params) => params.value || "-",
                type: "number",
              },
              {
                ...extraColumnsField("Stop Loss", "stopLoss", 1, 1),
                valueFormatter: (params) => params.value || "-",
                type: "number",
              },
              {
                ...extraColumnsField("Deviation", "deviation", 1, 1),
                valueFormatter: (params) => params.value || "-",
                type: "number",
              },

              {
                ...extraColumnsField("Volatility", "volatility", 1, 1),
                valueFormatter: (params) =>
                  handleCapitalize(handleRemoveUnderscore(params?.value)),
              },
              {
                ...extraColumnsField("Time Horizon", "timePeriod", 1, 1),
                valueFormatter: (params) =>
                  handleCapitalize(handleRemoveUnderscore(params?.value)),
              },

              {
                ...extraColumnsField("Rationale", "rationale", 1, 1),
                valueGetter: (params) => htmlToText(params?.row?.rationale),
              },
            ]}
          />
        </Show.When>
      ),
    },
    {
      show: true,
      title: "Research Files",
      element: (
        <Show.When
          isTrue={addDetailsData?.data?.researchFiles?.length !== 0}
          showNodata
        >
          <GKClientDataGrid
            name="assetResearchFiles"
            density="compact"
            columnsButton={false}
            pagination={false}
            rows={addDetailsData?.data?.researchFiles || []}
            columns={[
              {
                ...extraColumnsField("Name", "name", 1, 1),
                valueFormatter: (params) => params.value || "-",
              },
              {
                ...extraColumnsField("Uploaded By", "uploadedBy", 1, 1),
              },
              {
                ...extraColumnsField("Date", "createdOn", 1, 1),
                valueFormatter: (params) =>
                  dayjs(params.value).format("DD MMM YYYY hh:ss") || "-",
              },
              {
                ...extraColumnsField("Publish File", "visible ", 1, 1),
                headerAlign: "right",
                align: "right",
                renderCell: (params) => {
                  return (
                    <Switch
                      onChange={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handlePublishResearchFiles(params?.row.id);
                      }}
                      checked={params?.row?.visible}
                      disabled={researchUpdateFilesLoading}
                    />
                  );
                },
              },
              {
                ...extraColumnsField("Acion", "action", 1, 1),
                headerAlign: "right",
                align: "right",
                renderCell: (params) => {
                  return (
                    <GKButton
                      color="error"
                      size="extraSmall"
                      loading={researchFilesLoading}
                      variant="text"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteResearchFiles(params?.row.id);
                      }}
                    >
                      Delete
                    </GKButton>
                  );
                },
              },
            ]}
          />
        </Show.When>
      ),
    },
    {
      show: basketDetails?.managementStyle !== "Subscription",
      title: "Linked Model Portfolio",
      element: (
        <Show.When
          isTrue={addDetailsData?.data?.linkedVersions?.length !== 0}
          showNodata
        >
          <GKClientDataGrid
            name="linkedVersion"
            columnsButton={false}
            density="compact"
            pagination={false}
            getRowId={(pa) => pa?.itemId}
            rows={addDetailsData?.data?.linkedVersions || []}
            columns={[
              {
                ...extraColumnsField("Version", "version", 120, 1),
              },
              {
                type: "number",
                ...extraColumnsField("Weightage", "weightage", 1, 1),
              },
            ]}
          />
        </Show.When>
      ),
    },
  ];

  const tableData = [
    {
      title: "Latest Target",
      value: numberToCurrency(addDetailsData?.data?.targetPrice),
    },
    {
      title: "Stop Loss",
      value: numberToCurrency(addDetailsData?.data?.stopLoss),
    },
    {
      title: "Limit Price",
      value: numberToCurrency(addDetailsData?.data?.limitPrice),
    },
    {
      title: "Rec. Buy Price",
      value: numberToCurrency(addDetailsData?.data?.recommendedBuyPrice),
    },
    {
      title: "Deviation",
      value: `${addDetailsData?.data?.deviation || 0.0}%`,
    },
    {
      title: "Max. Investment Amount",
      value: numberToCurrency(addDetailsData?.data?.maxInvestedAmount),
    },
  ];

  return (
    <>
      <DashboardLayout
        title="Assets Details"
        loading={isFetching && !isRefetching}
      >
        <Grid2 container spacing={1}>
          <Grid2
            container
            spacing={1}
            bgcolor={"#F8F8F8"}
            size={12}
            borderRadius={2}
            p={2}
          >
            <Grid2 size={3} display={"flex"} alignItems={"center"}>
              <StatusField statusString={addDetailsData?.data?.assetStatus} />
            </Grid2>
            <Grid2
              size={9}
              textAlign={"right"}
              gap={1}
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
            >
              <IconButton color="error">
                <DeleteIcon />
              </IconButton>
              <Divider orientation="vertical" />
              <GKButton
                sx={{ p: 0 }}
                size="small"
                onClick={() => {
                  navigate(`/app/equity-basket/${id}/assets/${assetId}/edit`);
                }}
                startIcon={<Edit1Icon />}
              >
                Edit Details
              </GKButton>
              <Show.When
                isTrue={basketDetails?.managementStyle !== "Subscription"}
              >
                <Divider orientation="vertical" />
                <GKButton
                  sx={{ p: 0 }}
                  size="small"
                  onClick={() => {
                    setIsStatusModalOpen(true);
                  }}
                >
                  Set Portfolio Status
                </GKButton>
              </Show.When>
              <Show.When
                isTrue={
                  userData?.broker?.isRaAccess &&
                  basketDetails?.basketType !== "Model"
                }
              >
                <Divider orientation="vertical" />
              </Show.When>
              {userData?.broker?.isRaAccess &&
                basketDetails?.basketType !== "Model" && (
                  <GKButton
                    sx={{ p: 0 }}
                    size="small"
                    color={form.getValues("published") ? "error" : "success"}
                    onClick={() => {
                      form.setValue("published", !form.getValues("published"));
                      setTimeout(() => {
                        handleSubmit(
                          handleCreateFormData({
                            values: form.getValues(),
                            dirtyFields: form.formState.dirtyFields,
                          }),
                        );
                      }, 10);
                    }}
                  >
                    {form.getValues("published")
                      ? "Unpublish Asset"
                      : "Publish Asset"}
                  </GKButton>
                )}
            </Grid2>
            <Grid2 size={12}>
              <Typography fontWeight={600} fontSize={24}>
                {addDetailsData?.data?.companyName}
              </Typography>
              <Typography>
                CMP :{" "}
                <span
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {numberToCurrency(addDetailsData?.data?.cmp)}
                </span>
              </Typography>
            </Grid2>
            <Show.When
              isTrue={
                !isEmpty(addDetailsData?.data?.riskProfile) ||
                !isEmpty(addDetailsData?.data?.volatility) ||
                !isEmpty(addDetailsData?.data?.timePeriod)
              }
              showNodata
            >
              {[
                {
                  name: "Risk",
                  value: addDetailsData?.data?.riskProfile[0] || "-",
                  icon: RiskProfileIcon,
                  withSeperator: true,
                  iconColor: `var(--warning-700)`,
                },
                {
                  name: "Volatility",
                  value: addDetailsData?.data?.volatility || "-",
                  icon: VolatileIcon,
                  withSeperator: true,
                  iconColor: "var(--orange-700)",
                },
                {
                  name: "Time Horizon",
                  value: addDetailsData?.data?.timePeriod || "-",
                  icon: TermIcon,
                  withSeperator: false,
                  iconColor: "var(--blue-400)",
                },
              ]?.map((item) => {
                return (
                  <Show.When isTrue={!isEmpty(item.value)}>
                    <Box display={"flex"} alignItems={"center"} gap={0.5}>
                      <Typography fontSize={12} color="textSecondary">
                        {item.name}:
                      </Typography>
                      <Box display={"flex"} alignItems={"center"} gap={0.5}>
                        <item.icon color={item.iconColor} />
                        <Typography fontSize={12} fontWeight={600}>
                          {handleRemoveUnderscore(
                            handleCapitalize(item?.value),
                          ) || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    {item.withSeperator && (
                      <Divider orientation="vertical" sx={{ height: "20px" }} />
                    )}
                  </Show.When>
                );
              })}
            </Show.When>
            <Grid2 size={12}>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      {tableData?.map((dataItem) => (
                        <TableCell
                          style={{
                            borderRight: `1px solid var(--neutral-100)`,
                            padding: "16px",
                          }}
                          width={`${100 / tableData.length}%`}
                        >
                          <Typography color="#222" fontSize={12}>
                            {dataItem.title}
                          </Typography>
                          <Typography fontWeight={600} fontSize={16}>
                            {dataItem.value}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              onChange={(e, val) => {
                dispatch(TAB({ tabKey: "assetDetailsTabs", tabValue: val }));
              }}
              value={assetDetailsTabs}
            >
              {tabsData.map(
                (daItem) => daItem.show && <Tab label={daItem.title} />,
              )}
            </Tabs>
            <Divider />
          </Grid2>
          <Grid2 size={12}>{tabsData[assetDetailsTabs].element}</Grid2>
        </Grid2>
      </DashboardLayout>
      {isStatusModalOpen && (
        <AssetsStatusModal
          open={isStatusModalOpen}
          setOpen={setIsStatusModalOpen}
        />
      )}
      {showAssetModal && (
        <AssetHistoryDetailsModal
          open={showAssetModal}
          setOpen={setShowAssetModal}
          data={showAssetDetails}
        />
      )}
    </>
  );
};

export default AddPortfolioDetailsModal;
